/**
 * Centralized API error message handling.
 * Use when rejecting in the API layer and when displaying errors in catch blocks.
 */

const PHONE_VERIFICATION_REQUIRED = {
    match: /phone verification is required/i,
    // Says the one thing the user can act on. The previous copy — "Please complete your
    // profile to send inquiries" — sent people to a profile page that was already complete,
    // because the API gates on phone_verified_at rather than on any missing field.
    message: 'Verify your phone number to send inquiries.',
};

/** Maps known API error messages (or substrings) to user-friendly messages. */
const FRIENDLY_MESSAGE_MAP = [
    {
        match: /unable to find google place id/i,
        message: 'Invalid location provided. Please select a valid city from the suggestions.',
    },
    PHONE_VERIFICATION_REQUIRED,
];

/**
 * Replaces a raw API message with a friendlier one when we have a mapping.
 * @param {string} rawMessage - Message extracted from the API
 * @returns {string}
 */
function mapErrorMessages(rawMessage) {
    if (!rawMessage || typeof rawMessage !== 'string') return rawMessage;
    const trimmed = rawMessage.trim();
    for (const { match, message } of FRIENDLY_MESSAGE_MAP) {
        if (match.test(trimmed)) return message;
    }
    return trimmed;
}

/**
 * Extracts a raw message string from an error-like object (message, then errors.displayMessage,
 * then errors.messages). Single place for API error shape handling.
 *
 * @param {object} obj - Object that may have .message or .errors
 * @param {string} fallback - Value to return when nothing is found
 * @returns {string}
 */
function getMessageFromErrorObject(obj, fallback) {
    if (!obj || typeof obj !== 'object') return fallback;

    const { errors } = obj;
    const candidates = [
        obj.message,
        errors?.displayMessage,
        errors?.display_message,
        errors?.messages, // string case
        Array.isArray(errors?.messages) && errors.messages[0], // array case
    ];

    const found = candidates.find(c => typeof c === 'string' && c.trim());
    return found || fallback;
}

/**
 * Extracts a raw message string from a camelized API error response.
 * Used when building the reject payload in the API call() function.
 * Friendly-message mapping is applied only in getApiErrorMessage when the consumer displays the error.
 *
 * @param {object} camelized - Camelized JSON response (success: false)
 * @param {{ status?: number; statusText?: string }} responseStatus - Response status
 * @returns {string}
 */
export function extractMessageFromResponse(camelized, responseStatus = {}) {
    const fallback = responseStatus.statusText || 'Something went wrong. Please try again.';
    return getMessageFromErrorObject(camelized, fallback);
}

/**
 * Whether a failed response means the session is over and the user must sign in again.
 *
 * The API answers every missing, expired, invalid or blacklisted token with 401. A 403 means
 * "signed in, but not allowed to do this" — a permission or business rule the caller should show —
 * with one exception: a blocked email domain is refused on every authenticated request, so that
 * session can never succeed again. Logging out on other 403s destroyed unsaved work (BIZ-2512).
 */
export function isSessionEndedError(camelized, responseStatus = {}) {
    if (responseStatus.status === 401) return true;
    if (responseStatus.status !== 403) return false;
    return /email domain is not permitted/i.test(extractMessageFromResponse(camelized, responseStatus));
}

/** Inquiry send/create endpoints return 403 for unverified phones — not an auth failure. */
export function isPhoneVerificationRequiredError(camelized, responseStatus = {}) {
    if (responseStatus.status !== 403) return false;
    const message = extractMessageFromResponse(camelized, responseStatus);
    return PHONE_VERIFICATION_REQUIRED.match.test(message);
}

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Returns a user-facing message from any thrown value (API error, Error, string, etc.).
 * Use in catch blocks when showing errors to the user (e.g. enqueueSnackbar).
 *
 * @param {unknown} error - Value caught in catch (e)
 * @param {string} [fallback] - Optional fallback when no message can be extracted
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = DEFAULT_MESSAGE) {
    let raw = fallback;
    if (typeof error === 'string' && error.trim()) {
        raw = error;
    } else if (error && typeof error === 'object') {
        raw = getMessageFromErrorObject(error, fallback);
    }
    return mapErrorMessages(raw);
}
